import { isMacAppStore } from '@/lib/appDistribution';
import React, { useMemo, useRef, useState } from 'react';
import { ActivityIndicator, AppState, Linking, Platform, Pressable, ScrollView, TextInput, View, StyleSheet } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { configureLocalDesktop } from '@/lib/localDesktop';
import { filterPlugins } from '@/lib/browserPlugins';
import { pluginRuntime } from '@/lib/plugins/portable.cjs';
import { usePluginStore, readPluginState, refreshPlugins, refreshPluginConnections, disconnectPluginConnection, importPluginFolder, canImportPluginFolder } from '@/lib/plugins/pluginStore';

import { useModalStore } from '@/store/modal';
import { PluginButton, PluginMark, styles as sharedStyles } from './PluginControls';
import { usePluginDirectoryNavigation } from './PluginDirectorySidebar';

function accountStatus(pkg, mcp) {
  const endpoints = Object.values(pkg.mcpServers || {});
  if (!endpoints.length) return null;
  if (!mcp) return 'Check connection';
  const statuses = endpoints.map(spec => {
    const service = mcp.servers?.find(server => server.url === spec.url);
    if (!service || !(service.custom ? mcp.customAvailable : mcp.available) || service.available === false) return 'unavailable';
    return mcp.connections?.find(connection => connection.server === service.id)?.status || 'disconnected';
  });
  if (statuses.includes('unavailable')) return 'Unavailable';
  if (statuses.every(status => status === 'connected')) return 'Connected';
  if (statuses.includes('reconnect')) return 'Reconnect needed';
  if (statuses.includes('pending')) return 'Waiting for sign-in';
  return 'Connect account';
}

function PluginWorkspace({ userId, embeddedInPage }) {
  const { colors } = useTheme();
  const openModal = useModalStore(state => state.actions.openModal);
  const dialogSequence = useRef(0);
  const openDialog = props => openModal('PluginDetailsModal', { userId, key: `${userId}:${++dialogSequence.current}`, ...props });
  const setDetail = initialDetail => openDialog({ initialDetail });
  const revision = usePluginStore(state => state.revision);
  const [query, setQuery] = useState(''), [width, setWidth] = useState(900);
  const [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const pending = useRef(false), mounted = useRef(true);
  const view = usePluginDirectoryNavigation(s => s.view);
  const setView = usePluginDirectoryNavigation(s => s.setView);

  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const saved = useMemo(() => {
    try { return readPluginState(userId); }
    catch (e) { return { records: [], error: e.message || 'Saved plugins could not be read.' }; }
  }, [userId, revision]);
  const isMac = Platform.OS === 'macos';
  const browserWorkflows = isMac && !isMacAppStore();
  const records = saved.records.filter(record => browserWorkflows || pluginRuntime(record.package) !== 'mac-browser');
  const catalog = (saved.catalog || []).filter(entry => browserWorkflows || entry.runtime !== 'mac-browser');
  const run = async action => {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError('');
    try { await action(); }
    catch (e) { if (mounted.current) setError(e.message || 'Could not update plugins.'); }
    finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  const refresh = async () => { await refreshPlugins(userId); await refreshPluginConnections(userId); };
  React.useEffect(() => { void run(refresh); }, [userId]);
  React.useEffect(() => {
    const update = () => { void run(() => refreshPluginConnections(userId)); };
    const subscription = AppState.addEventListener('change', state => { if (state === 'active') update(); });
    return () => subscription.remove();
  }, [userId]);
  const showBundled = entry => setDetail({ package: entry.package, source: 'bundled' });
  const chooseFolder = () => run(async () => {
    const pkg = await importPluginFolder(userId);
    if (pkg && mounted.current) setDetail({ package: pkg, source: 'local' });
  });
  const matches = filterPlugins(catalog, query);
  const installed = records.filter(record => {
    const display = record.source === 'bundled' && catalog.find(entry => entry.id === record.package.manifest.name);
    return filterPlugins([{ name: display?.name || record.package.manifest.name, category: '', description: record.package.manifest.description || '' }], query).length;
  });

  const installedStatus = item => !item.enabled ? 'Off' : pluginRuntime(item.package) === 'mac-browser' && !isMac
    ? 'Available on Mac' : accountStatus(item.package, saved.mcp) || 'Enabled';
  const Container = embeddedInPage ? View : ScrollView;

  const renderDiscover = () => {
    const groups = {
      'Connected apps': matches.filter(p => p.runtime !== 'mac-browser' && p.package.hasMcp),
      'Skills': matches.filter(p => p.runtime !== 'mac-browser' && !p.package.hasMcp),
      'Browser workflows': matches.filter(p => p.runtime === 'mac-browser')
    };
    return (
      <View style={styles.section}>
        {Object.entries(groups).map(([title, items]) => {
          if (!items.length) return null;
          return (
            <View key={title} style={styles.section}>
              <Text size="l" weight="medium" accessibilityRole="header">{title}</Text>
              <View style={styles.grid}>
                {items.map(plugin => {
                  const added = records.find(item => item.id === `bundled:${plugin.id}`);
                  return (
                    <Pressable
                      key={plugin.id}
                      accessibilityRole="button"
                      accessibilityLabel={`${added ? 'Manage' : 'View'} ${plugin.name} plugin`}
                      onPress={() => showBundled(plugin)}
                      style={({ hovered }) => [styles.compactRow, { width: width >= 680 ? '48.5%' : '100%', backgroundColor: hovered ? colors.solid_color_secondary : 'transparent' }]}
                    >
                      <PluginMark entry={plugin} size={32} />
                      <View style={styles.grow}>
                        <Text size="m" weight="medium">{plugin.name}</Text>
                        <Text size="s" color="secondary" numberOfLines={2}>{plugin.description}</Text>
                        {added && plugin.package.hasMcp && <Text size="xs" color="tertiary">Account connection · {installedStatus(added)}</Text>}
                      </View>
                      <Icon name={added ? 'ellipsis' : 'plus'} size={16} color={colors.text_color_secondary} />
                    </Pressable>
                  );
                })}
              </View>
            </View>
          );
        })}
        {saved.loaded && !matches.length && <Text size="s" color="secondary">No plugins match “{query}”. Try another search.</Text>}
      </View>
    );
  };

  const renderInstalled = () => (
    <View style={styles.section}>
      <Text size="l" weight="medium" accessibilityRole="header">Installed{records.length ? ` · ${records.length}` : ''}</Text>
      {!!installed.length ? (
        <View style={styles.grid}>
          {installed.map(item => {
            const display = item.source === 'bundled' && catalog.find(plugin => plugin.id === item.package.manifest.name);
            return (
              <Pressable
                key={item.id}
                accessibilityRole="button"
                accessibilityLabel={`Manage ${display?.name || item.package.manifest.name}`}
                onPress={() => setDetail({ package: item.package, source: item.source })}
                style={({ hovered }) => [styles.compactRow, { width: width >= 680 ? '48.5%' : '100%', backgroundColor: hovered ? colors.solid_color_secondary : 'transparent' }]}
              >
                <PluginMark entry={display} size={32} />
                <View style={styles.grow}>
                  <Text size="s" weight="medium">{display?.name || item.package.manifest.name}</Text>
                  <Text size="xs" color="secondary">{item.source === 'local' ? 'Imported · ' : ''}{installedStatus(item)}</Text>
                </View>
                <Icon name="ellipsis" size={16} color={colors.text_color_secondary} />
              </Pressable>
            );
          })}
        </View>
      ) : (
        <Text size="s" color="secondary">{query ? 'No installed plugins match your search.' : saved.loaded ? 'Install a plugin below to add skills to Chat and Work.' : 'Loading your plugins…'}</Text>
      )}
    </View>
  );

  const renderConnections = () => {
    const activeConnections = (saved.mcp?.connections || []).filter(c => c.status !== 'disconnected').filter(c => { const service = saved.mcp?.servers?.find(s => s.id === c.server); return filterPlugins([{name: service?.name || c.server, description: c.status}], query).length; });
    if (!activeConnections.length) return <Text size="s" color="secondary">No active connections.</Text>;
    return (
      <View style={styles.section}>
        <Text size="l" weight="medium" accessibilityRole="header">Connections</Text>
        <Text size="s" color="secondary">Manage connected accounts and MCP servers independently of your installed plugins.</Text>
        <View style={{ gap: 4 }}>
          {activeConnections.map(connection => {
            const service = saved.mcp.servers.find(server => server.id === connection.server);
            const entry = catalog.find(p => Object.values(p.package.mcpServers || {}).some(s => s.url === service?.url));
            const statusText = ({ connected: 'Connected', pending: 'Waiting for sign-in', reconnect: 'Reconnect needed' })[connection.status];
            return (
              <View key={connection.server} style={[styles.compactRow, { width: '100%', minHeight: 72, borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary, flexWrap: 'wrap' }]}>
                <PluginMark entry={entry} size={32} />
                <View style={styles.grow}>
                  <Text size="s" weight="medium">{service?.name || connection.server}</Text>
                  <Text size="xs" color="secondary">Account connection · {statusText}</Text>
                </View>
                <PluginButton
                  compact label="Disconnect" accessibilityLabel={`Disconnect ${service?.name || connection.server} account`}
                  disabled={busy}
                  onPress={() => run(() => disconnectPluginConnection(userId, connection.server))}
                />
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <Container style={{ ...(embeddedInPage ? {} : { flex: 1 }), backgroundColor: colors.solid_color_primary }}
      {...(!embeddedInPage && { contentContainerStyle: [styles.scroll, { padding: width < 600 ? 20 : 32 }], keyboardShouldPersistTaps: 'handled' })}>
      <View style={styles.content} onLayout={event => setWidth(event.nativeEvent.layout.width)}>
        <View style={styles.heading}>
          {!embeddedInPage && <View style={styles.grow}>
            <Text size="xxl" weight="medium" accessibilityRole="header">Plugins</Text>
            <Text size="m" color="secondary">Connect apps and add skills to Chat and Work.</Text></View>}
        <View style={[styles.search, { width: width >= 680 ? 240 : '100%', borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary }]}>
          <Icon name="magnifying-glass" size={17} color={colors.text_color_secondary} />
          <TextInput value={query} onChangeText={setQuery} placeholder="Search plugins" accessibilityLabel="Search plugins"
            placeholderTextColor={colors.text_color_tertiary} autoCorrect={false}
            style={{ flex: 1, color: colors.text_color_primary, fontSize: 16, padding: 0 }} />
          {!!query && <PluginButton compact label="Clear" onPress={() => setQuery('')} />}
        </View>
          <PluginButton compact label="Refresh" onPress={() => run(refresh)} disabled={busy} />
          <PluginButton compact primary label="Add MCP server" onPress={() => { setError(''); openDialog({ initialCustom: {} }); }} disabled={busy || !saved.loaded || !saved.mcp?.customAvailable} />
          {isMac && canImportPluginFolder && <PluginButton compact label="Add plugin…" onPress={chooseFolder} disabled={busy || !saved.loaded} />}
        </View>



        <View style={{ padding: 16, borderRadius: 12, backgroundColor: colors.solid_color_secondary }}>
          <Text size="s">{pluginHelp}</Text>
        </View>
        <View style={styles.tabBar}>
          {['discover', 'installed', 'connections'].map(tab => (
            <Pressable key={tab} accessibilityRole="tab" accessibilityLabel={tab.charAt(0).toUpperCase() + tab.slice(1)} accessibilityState={{selected:view === tab}} onPress={() => setView(tab)} style={[styles.tab, view === tab && { backgroundColor: colors.solid_color_secondary }]}>
              <Text size="s" weight={view === tab ? 'bold' : 'medium'} color={view === tab ? 'primary' : 'secondary'}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</Text>
            </Pressable>
          ))}
        </View>

        {browserWorkflows && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text size="s" color="secondary" style={{ flex: 1 }}>Browser workflows use your Mac's sign-ins and browser access. Keep Gab AI open while they run.</Text>
          <PluginButton compact label="Browser setup" onPress={() => run(configureLocalDesktop)} disabled={busy} />
        </View>}


        {!!(error || saved.error) && <Text accessibilityRole="alert" size="s" color="danger">{error || saved.error}</Text>}
        {saved.warnings?.map(warning => <Text key={warning} size="s" color="secondary">{warning}</Text>)}
        {busy && <ActivityIndicator accessibilityLabel="Updating plugins" />}
        {saved.loaded && saved.mcp && !saved.mcp.customAvailable && <Text size="s" color="secondary">Custom MCP servers need a Gab AI server update.</Text>}

        {view === 'discover' && renderDiscover()}
        {view === 'installed' && renderInstalled()}
        {view === 'connections' && renderConnections()}

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingTop: 16 }}>
          <Text size="xs" color="tertiary">Your skills and connected accounts are available in Chat and Work.</Text>
          <Pressable accessibilityRole="link" accessibilityLabel="About the plugin standard" onPress={() => run(() => Linking.openURL('https://agent-plugins.org/'))}>
            <Text size="xs" color="secondary">About plugins ↗</Text>
          </Pressable>
        </View>
      </View>
    </Container>
  );
}

export default function PluginsScreen({ embeddedInPage = false }) {
  const user = useAuthStore(state => state.user);
  if (!user?.id || user.flags?.isGhost) return <View style={{ padding: 32, gap: 12 }}><Text size="xxxl" weight="bold">Plugins</Text><Text>Sign in to manage your plugins.</Text></View>;
  return <PluginWorkspace key={String(user.id)} userId={String(user.id)} embeddedInPage={embeddedInPage} />;
}

const pluginHelp = 'Connect your apps so Gab AI can find information and get things done for you in Chat and Work, like searching email, organizing files, scheduling meetings, or updating documents. You control your connections and approve actions when required.';

const styles = { ...sharedStyles, ...StyleSheet.create({
  scroll: { padding: 36, paddingTop: 32, paddingBottom: 56 },
  content: { width: '100%', maxWidth: 960, alignSelf: 'center', gap: 28 },
  heading: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  search: { minHeight: 36, flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, borderWidth: 0, borderRadius: 22 },
  tabBar: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  tab: { minHeight: 36, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', columnGap: 16, rowGap: 6 },
  compactRow: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 12, borderRadius: 12, minHeight: 92 },
}) };
